export const OFFICIAL_CONTEST_WINDOW = Object.freeze({ startDate: "2026-10-06", endDate: "2026-12-01" });
export const PRIZE_COUNT = 5;
export const SHOP_TIME_ZONE = "America/Denver";

export const INELIGIBLE_REASON_LABELS = {
    not_video: "Not a video",
    not_approved: "Not approved in Trybe",
    unmatched_submission: "No Trybe submission for this ID",
    non_positive_gmv: "No net sales in range",
};

export const SUBMISSION_STATUS_LABELS = {
    approved: "Approved",
    pending: "Pending review",
    rejected: "Rejected",
    revision_requested: "Revision requested",
};

const YMD = /^(\d{4})-(\d{2})-(\d{2})/;

export const formatYmd = (value) => {
    const match = YMD.exec(String(value || ""));
    return match ? `${match[2]}/${match[3]}/${match[1]}` : "—";
};

export const formatZonedDate = (value) => {
    if (!value) return "—";
    if (YMD.test(String(value)) && String(value).length === 10) return formatYmd(value);
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "—";
    return formatYmd(ymdInZone(date, SHOP_TIME_ZONE));
};

const toNumber = (value) => {
    if (value === null || value === undefined || value === "") return null;
    const number = Number(value);
    return Number.isFinite(number) ? number : null;
};

export const formatMoney = (value, { cents = true } = {}) => {
    const number = toNumber(value);
    if (number === null) return "—";
    return number.toLocaleString("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: cents ? 2 : 0,
        maximumFractionDigits: cents ? 2 : 0,
    });
};

export const formatSignedMoney = (value) => {
    const number = toNumber(value);
    if (number === null) return "—";
    if (Math.abs(number) < 0.005) return "$0.00";
    return `${number > 0 ? "+" : "−"}${formatMoney(Math.abs(number))}`;
};

export const formatPercent = (value) => {
    const number = toNumber(value);
    if (number === null) return "—";
    const pct = number * 100;
    if (Math.abs(pct) < 0.05) return "0.0%";
    return `${pct > 0 ? "+" : "−"}${Math.abs(pct).toFixed(1)}%`;
};

export const formatRoas = (value) => {
    const number = toNumber(value);
    return number === null ? "—" : `${number.toFixed(2)}x`;
};

export const formatCount = (value) => {
    const number = toNumber(value);
    return number === null ? "—" : number.toLocaleString("en-US");
};

export const ymdInZone = (date = new Date(), timeZone = SHOP_TIME_ZONE) => {
    const parts = Object.fromEntries(new Intl.DateTimeFormat("en-US", {
        timeZone, year: "numeric", month: "2-digit", day: "2-digit",
    }).formatToParts(date).map((part) => [part.type, part.value]));
    return `${parts.year}-${parts.month}-${parts.day}`;
};

export const shiftYmd = (ymd, days) => {
    const date = new Date(`${ymd}T12:00:00Z`);
    date.setUTCDate(date.getUTCDate() + days);
    return date.toISOString().slice(0, 10);
};

export const DATE_PRESETS = [
    { id: "official", label: "Official contest window" },
    { id: "last7", label: "Last 7 days" },
    { id: "last30", label: "Last 30 days" },
    { id: "mtd", label: "Month to date" },
];

export const presetRange = (presetId, now = new Date()) => {
    const yesterday = shiftYmd(ymdInZone(now), -1);
    switch (presetId) {
        case "last7": return { startDate: shiftYmd(yesterday, -6), endDate: yesterday };
        case "last30": return { startDate: shiftYmd(yesterday, -29), endDate: yesterday };
        case "mtd": return { startDate: `${yesterday.slice(0, 8)}01`, endDate: yesterday };
        case "official":
        default: return { ...OFFICIAL_CONTEST_WINDOW };
    }
};

export const matchPreset = ({ startDate, endDate }, now = new Date()) => {
    const match = DATE_PRESETS.find((preset) => {
        const range = presetRange(preset.id, now);
        return range.startDate === startDate && range.endDate === endDate;
    });
    return match ? match.id : "custom";
};

export const presetLabel = (presetId, range) => {
    if (presetId === "official") return `Official window · ${formatYmd(OFFICIAL_CONTEST_WINDOW.startDate)}–${formatYmd(OFFICIAL_CONTEST_WINDOW.endDate)}`;
    const preset = DATE_PRESETS.find((item) => item.id === presetId);
    if (preset) return preset.label;
    return `${formatYmd(range.startDate)}–${formatYmd(range.endDate)}`;
};

export const entrySearchText = (entry) => [
    entry.creatorName,
    entry.trybeId,
    entry.programName,
    ...(entry.products || []),
    ...(entry.pillars || []),
    ...(entry.angles || []),
    ...(entry.adNames || []),
].filter(Boolean).join(" ").toLowerCase();

export const reconciliationSearchText = (row) => [
    row.creatorName,
    ...(row.trybeIds || []),
].filter(Boolean).join(" ").toLowerCase();

export const filterEntries = (entries, { query = "", eligibility = "all" } = {}) => {
    const q = query.trim().toLowerCase();
    return (entries || []).filter((entry) => {
        if (eligibility === "eligible" && !entry.eligible) return false;
        if (eligibility === "ineligible" && entry.eligible) return false;
        return !q || entrySearchText(entry).includes(q);
    });
};

export const sortRows = (rows, sort, accessors) => {
    const accessor = sort && accessors?.[sort.key];
    if (!Array.isArray(rows) || typeof accessor !== "function") return rows;

    const direction = sort.direction === "desc" ? -1 : 1;
    const isMissing = (value) => value === null || value === undefined || value === "" || Number.isNaN(value);
    return rows
        .map((row, index) => ({ row, index, value: accessor(row) }))
        .sort((left, right) => {
            const leftMissing = isMissing(left.value);
            const rightMissing = isMissing(right.value);
            if (leftMissing || rightMissing) {
                if (leftMissing && rightMissing) return left.index - right.index;
                return leftMissing ? 1 : -1;
            }

            let comparison = 0;
            if (typeof left.value === "string" && typeof right.value === "string") {
                comparison = left.value.localeCompare(right.value, undefined, { sensitivity: "accent" });
            } else if (
                (typeof left.value === "number" || typeof left.value === "boolean") &&
                (typeof right.value === "number" || typeof right.value === "boolean")
            ) {
                comparison = Number(left.value) - Number(right.value);
            } else {
                comparison = String(left.value).localeCompare(String(right.value), undefined, { sensitivity: "accent" });
            }
            return comparison === 0 ? left.index - right.index : comparison * direction;
        })
        .map(({ row }) => row);
};

/** Top-N view: with one prize per creator use the server's winners; otherwise the top eligible entries. */
export const topEntries = (data, { onePrizePerCreator = true, count = PRIZE_COUNT } = {}) => {
    if (!data) return [];
    if (onePrizePerCreator) return (data.winners || []).slice(0, count);
    return (data.entries || []).filter((entry) => entry.eligible).slice(0, count);
};

export const varianceTone = (variancePct, variance) => {
    const pct = toNumber(variancePct);
    const abs = toNumber(variance);
    if (pct === null) return abs && Math.abs(abs) >= 0.01 ? "warn" : "ok";
    if (Math.abs(pct) <= 0.02) return "ok";
    if (Math.abs(pct) <= 0.1) return "warn";
    return "bad";
};
